import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  deleteDoc, 
  query, 
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { AppConcept } from '../types';

// Firebase configuration (reusing from AuthContext)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase (will use existing instance if already initialized)
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

export interface FirebaseConcept extends Omit<AppConcept, 'createdAt'> {
  userId: string;
  createdAt: Timestamp;
}

export const saveConcept = async (concept: AppConcept, userId: string): Promise<void> => {
  try {
    console.log('💾 Attempting to save concept to Firebase:', {
      conceptId: concept.id,
      appName: concept.appName,
      userId: userId,
      hasValidId: !!concept.id
    });
    
    const conceptWithUser = {
      ...concept,
      userId,
      createdAt: serverTimestamp()
    };
    
    await setDoc(doc(db, 'concepts', concept.id), conceptWithUser);
    console.log('✅ Concept successfully saved to Firebase with ID:', concept.id);
  } catch (error) {
    console.error('❌ Error saving concept to Firebase:', error);
    console.error('❌ Concept data that failed to save:', concept);
    throw error;
  }
};

export const loadUserConcepts = async (userId: string, retryCount = 0): Promise<AppConcept[]> => {
  const maxRetries = 2;
  
  try {
    console.log(`📥 Loading concepts from Firebase for user: ${userId} (attempt ${retryCount + 1})`);
    
    const q = query(
      collection(db, 'concepts'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const concepts: AppConcept[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data() as FirebaseConcept;
      console.log('📄 Loading concept from Firebase:', doc.id, data.appName);
      
      // Handle null createdAt from serverTimestamp()
      let createdAtDate: Date;
      if (data.createdAt && typeof data.createdAt.toDate === 'function') {
        createdAtDate = data.createdAt.toDate();
      } else {
        // Fallback to current time if createdAt is null or invalid
        createdAtDate = new Date();
        console.warn('⚠️ Using fallback date for concept:', doc.id);
      }
      
      concepts.push({
        ...data,
        createdAt: createdAtDate
      });
    });
    
    console.log(`✅ Successfully loaded ${concepts.length} concepts from Firebase for user:`, userId);
    return concepts;
  } catch (error) {
    console.error(`❌ Error loading concepts from Firebase (attempt ${retryCount + 1}):`, error);
    
    // Check if it's a network/connection error and we can retry
    const isNetworkError = error instanceof Error && (
      error.message.includes('Failed to fetch') ||
      error.message.includes('network') ||
      error.message.includes('offline') ||
      error.message.includes('transport errored')
    );
    
    if (isNetworkError && retryCount < maxRetries) {
      console.log(`🔄 Retrying Firebase load (${retryCount + 1}/${maxRetries})...`);
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
      return loadUserConcepts(userId, retryCount + 1);
    }
    
    console.error('❌ Failed query details - userId:', userId);
    throw error;
  }
};

export const deleteConcept = async (conceptId: string, userId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'concepts', conceptId));
    console.log('✅ Concept deleted from Firebase:', conceptId);
  } catch (error) {
    console.error('❌ Error deleting concept from Firebase:', error);
    throw error;
  }
};

export const updateConcept = async (concept: AppConcept, userId: string): Promise<void> => {
  try {
    const conceptWithUser = {
      ...concept,
      userId,
      // Convert createdAt back to Timestamp for Firebase
      createdAt: Timestamp.fromDate(new Date(concept.createdAt))
    };
    
    await setDoc(doc(db, 'concepts', concept.id), conceptWithUser, { merge: true });
    console.log('✅ Concept updated in Firebase:', concept.id);
  } catch (error) {
    console.error('❌ Error updating concept in Firebase:', error);
    throw error;
  }
};

// Migration function to move localStorage data to Firebase
export const migrateLocalStorageToFirebase = async (userId: string): Promise<boolean> => {
  try {
    const localData = localStorage.getItem('showerThoughtsConcepts');
    if (!localData) return false;
    
    const concepts: AppConcept[] = JSON.parse(localData);
    console.log(`🔄 Migrating ${concepts.length} concepts from localStorage to Firebase...`);
    
    // Try to save all concepts first
    const savePromises = concepts.map(concept => saveConcept(concept, userId));
    await Promise.all(savePromises);
    
    // Only clear localStorage after ALL concepts are successfully saved
    // And verify they're actually in Firebase by loading them back
    const verifyLoad = await loadUserConcepts(userId);
    if (verifyLoad.length === concepts.length) {
      localStorage.removeItem('showerThoughtsConcepts');
      console.log('✅ Migration completed, localStorage cleared after verification');
      return true;
    } else {
      console.warn('⚠️ Migration verification failed, keeping localStorage as backup');
      return false;
    }
  } catch (error) {
    console.error('❌ Error during migration:', error);
    console.log('💾 Keeping localStorage data due to migration failure');
    // Don't throw here - migration failure shouldn't break the app
    // Keep localStorage data if migration fails
    return false;
  }
};